
import api, { LIST, STOREFRONT_PAGES, STOREFRONT_SECTIONS } from '../Api'
import { ICollectionResponse } from "../../models/ICollectionResponse";
import { IStorefrontPage } from "../../models/Storefront";
import { IActionResponse } from "../../dal/BaseDAL";

// Custom Pages data layer. Pages are a first-class API resource (server-side slug validation +
// delete cascade), preview-resolved server-side by the storefront id. See the migration plan
// (ac-obsidian/ac-main/claude/plans/storefront-pages-cosmos-container.md).
const storefrontPageApi = api.injectEndpoints({
    endpoints: (build) => ({
        // GET the storefront's pages (preview-resolved server-side, ordered by sortOrder). The API
        // returns a CollectionResponse ({ items, total }) like the sibling sections/banners lists;
        // unwrap to the bare page array the consumers expect.
        getStorefrontPages: build.query<IStorefrontPage[], string>({
            query: (arg) => `storefront/storefronts/${arg}/pages`,
            transformResponse: (response: ICollectionResponse<IStorefrontPage>) => response.items,
            providesTags: (_result, _error, storefront) => [
                { type: STOREFRONT_PAGES, id: storefront },
                { type: STOREFRONT_PAGES, id: LIST },
            ]
        }),
        // Single upsert. The server validates slug format/reserved/uniqueness and returns
        // success:false + message on violation; the response id is the page id.
        saveStorefrontPage: build.mutation<IActionResponse, { page: IStorefrontPage, storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/pages`,
                method: 'PUT',
                body: arg.page
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_PAGES, id: arg.storefront },
                { type: STOREFRONT_PAGES, id: LIST },
            ]
        }),
        // Batch upsert-only (reorder + exit-edit-mode dirty saves). No implicit deletes.
        saveStorefrontPages: build.mutation<IActionResponse, { pages: IStorefrontPage[], storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/pages/batch`,
                method: 'PUT',
                body: arg.pages
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_PAGES, id: arg.storefront },
                { type: STOREFRONT_PAGES, id: LIST },
            ]
        }),
        // Delete a page. The server cascades: it also deletes every section and banner belonging to
        // the page. We invalidate the STOREFRONT_SECTIONS type-filtered pickers (product-detail /
        // dynamic-product section dropdowns) so they drop the cascaded sections. We deliberately do
        // NOT invalidate the storefront's main section list tag ({ id: storefront } / LIST): delete
        // is only reachable inside Edit Mode, where StorefrontPagesEditorPage holds an in-progress
        // `sections` working list; a refetch of that list would clobber unsaved edits on other pages.
        // The page component instead syncs its sections + banners working state manually on delete
        // (banners have no RTK tags anyway — optimistic-only).
        deleteStorefrontPage: build.mutation<IActionResponse, { storefront: string, pageId: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/pages/${arg.pageId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_PAGES, id: arg.storefront },
                { type: STOREFRONT_PAGES, id: LIST },
                { type: STOREFRONT_SECTIONS, id: 'productDetails' },
                { type: STOREFRONT_SECTIONS, id: 'dynamicProducts' },
            ]
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontPagesQuery,
    useSaveStorefrontPageMutation,
    useSaveStorefrontPagesMutation,
    useDeleteStorefrontPageMutation,
} = storefrontPageApi
